import { MessageService } from 'primeng/api';
import { SignupParams } from 'app/components/operator/operator-signup/signup-params';
import { XpressDocumentTypeEnum } from '@shared/enums/xpress-document-type.enum';

export type OperatorVehicleUploadField =
    | 'VehicleFrontImageUrl'
    | 'VehicleBackImageUrl'
    | 'VehicleRightSideImageUrl'
    | 'VehicleLeftSideImageUrl'
    | 'LTOOfficialReceiptImageUrl'
    | 'CertificateRegistrationImageUrl'
    | 'SPAImageUrl'
    | 'ProvisionalAuthorityImageUrl'
    | 'CertificatePublicConvenienceImageUrl';

export const OPERATOR_VEHICLE_FIELD_TYPE_MAP: Record<OperatorVehicleUploadField, XpressDocumentTypeEnum> = {
    VehicleFrontImageUrl: XpressDocumentTypeEnum.VehiclePhoto,
    VehicleBackImageUrl: XpressDocumentTypeEnum.VehiclePhoto,
    VehicleRightSideImageUrl: XpressDocumentTypeEnum.VehiclePhoto,
    VehicleLeftSideImageUrl: XpressDocumentTypeEnum.VehiclePhoto,
    LTOOfficialReceiptImageUrl: XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration,
    CertificateRegistrationImageUrl: XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration,
    SPAImageUrl: XpressDocumentTypeEnum.NotarizedSPA,
    ProvisionalAuthorityImageUrl: XpressDocumentTypeEnum.Pa,
    CertificatePublicConvenienceImageUrl: XpressDocumentTypeEnum.Cpc
};

// DocumentFileType: FrontSide=1, BackSide=2, Attachment=3, MultiPage=4
// Distinct per slot so UploadImageAsync replace-by-type does not wipe another photo/OR-CR file.
export const OPERATOR_VEHICLE_FIELD_FILE_TYPE_MAP: Partial<Record<OperatorVehicleUploadField, number>> = {
    VehicleFrontImageUrl: 1,
    VehicleBackImageUrl: 2,
    VehicleRightSideImageUrl: 3,
    VehicleLeftSideImageUrl: 4,
    LTOOfficialReceiptImageUrl: 1,
    CertificateRegistrationImageUrl: 2
};

/** Legal-doc categories that accept multiple files. Vehicle photos stay single-slot. */
export const OPERATOR_VEHICLE_MULTI_FILE_FIELDS: ReadonlySet<OperatorVehicleUploadField> = new Set([
    'LTOOfficialReceiptImageUrl',
    'CertificateRegistrationImageUrl',
    'SPAImageUrl',
    'ProvisionalAuthorityImageUrl',
    'CertificatePublicConvenienceImageUrl'
]);

/** Max image files allowed per multi-upload legal-doc category. */
export const OPERATOR_VEHICLE_MAX_MULTI_FILES = 3;

export const OPERATOR_VEHICLE_ACCEPTED_FILE_INPUT =
    'image/jpeg,image/jpg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf';

/** DocumentFileType.Pdf = 5 */
const DOCUMENT_FILE_TYPE_PDF = 5;

/** Negative temp ids for locally staged (not yet uploaded) files. */
let nextStagedFileTempId = -1;

export const isPdfUploadFile = (file: File): boolean =>
    file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');

export const isPdfDocumentFileUrl = (url: string, documentFileType?: number | null): boolean =>
    Number(documentFileType) === DOCUMENT_FILE_TYPE_PDF
    || /\.pdf(\?|#|$)/i.test(url || '');

/** Matches ImageService ownership types that reset Approved/Rejected vehicles to Pending. */
export const isOperatorVehicleOwnershipUploadField = (fieldName: OperatorVehicleUploadField): boolean => {
    const documentType = OPERATOR_VEHICLE_FIELD_TYPE_MAP[fieldName];
    return documentType === XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration
        || documentType === XpressDocumentTypeEnum.NotarizedSPA
        || documentType === XpressDocumentTypeEnum.Pa
        || documentType === XpressDocumentTypeEnum.Cpc
        || documentType === XpressDocumentTypeEnum.PaOrCpc;
};

export interface UploadedDocumentMeta {
    id: number;
    type: XpressDocumentTypeEnum;
    url: string;
    /** Set for PDF only — from XpressDocument.Name / upload FileName. Empty for images. */
    fileName: string;
    isPdf: boolean;
    /** Plate (DocumentNumber) the file was uploaded under — must match the saved vehicle plate. */
    documentNumber?: string;
    /** Local File staged in the UI; uploaded only on Submit. */
    file?: File;
    /** True when this row is a local preview and not yet persisted. */
    isLocal?: boolean;
}

export type UploadedDocumentMetaMap = Partial<Record<OperatorVehicleUploadField, UploadedDocumentMeta[]>>;

export const OPERATOR_VEHICLE_UPLOAD_FIELDS: OperatorVehicleUploadField[] = [
    'VehicleFrontImageUrl',
    'VehicleBackImageUrl',
    'VehicleRightSideImageUrl',
    'VehicleLeftSideImageUrl',
    'LTOOfficialReceiptImageUrl',
    'CertificateRegistrationImageUrl',
    'SPAImageUrl',
    'ProvisionalAuthorityImageUrl',
    'CertificatePublicConvenienceImageUrl'
];

export const normalizeOperatorVehiclePlate = (plate: string | null | undefined): string =>
    (plate || '').trim().toUpperCase();

export interface OperatorVehicleDocumentUploadApi {
    uploadImage(data: FormData, messageService: MessageService): Promise<any>;
    deleteImage(data: any, messageService: MessageService): Promise<any>;
}

export const getOperatorVehicleUploadedFiles = (
    uploadedMeta: UploadedDocumentMetaMap,
    fieldName: OperatorVehicleUploadField
): UploadedDocumentMeta[] => uploadedMeta[fieldName] ?? [];

/** True when the category already has a PDF (further uploads are blocked until it is removed). */
export const hasOperatorVehiclePdfUploaded = (
    uploadedMeta: UploadedDocumentMetaMap,
    fieldName: OperatorVehicleUploadField
): boolean => getOperatorVehicleUploadedFiles(uploadedMeta, fieldName).some(file => file.isPdf);

/** True when the category already has the max number of files (no more uploads allowed). */
export const hasOperatorVehicleMaxFilesUploaded = (
    uploadedMeta: UploadedDocumentMetaMap,
    fieldName: OperatorVehicleUploadField
): boolean =>
    OPERATOR_VEHICLE_MULTI_FILE_FIELDS.has(fieldName)
    && getOperatorVehicleUploadedFiles(uploadedMeta, fieldName).length >= OPERATOR_VEHICLE_MAX_MULTI_FILES;

export const hasOperatorVehicleStagedUploads = (uploadedMeta: UploadedDocumentMetaMap): boolean =>
    Object.values(uploadedMeta)
        .flatMap(files => files ?? [])
        .some(file => !!file?.isLocal && !!file?.file);

export const syncOperatorVehicleUploadFieldUrl = (
    model: Record<string, any>,
    fieldName: OperatorVehicleUploadField,
    files: UploadedDocumentMeta[]
): void => {
    model[fieldName] = files.length > 0 ? files[0].url : null;
    model[fieldName + 'File'] = null;
};

export const validateUploadFile = (file: File, messageService: MessageService): boolean => {
    if (file.size > SignupParams.MAX_UPLOAD_FILE_SIZE) {
        messageService.add({ severity: 'error', summary: 'Error', detail: 'The file size exceeds 5 MB' });
        return false;
    }

    if (!SignupParams.ACCEPTED_FILE_TYPES.includes(file.type)) {
        messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Unsupported file format. Please upload a JPEG, PNG, or PDF file'
        });
        return false;
    }

    return true;
};

const revokeLocalPreviewUrl = (url: string | null | undefined): void => {
    if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
    }
};

const createStagedDocumentMeta = (
    file: File,
    fieldName: OperatorVehicleUploadField,
    plate: string
): UploadedDocumentMeta => {
    const isPdf = isPdfUploadFile(file);
    return {
        id: nextStagedFileTempId--,
        type: OPERATOR_VEHICLE_FIELD_TYPE_MAP[fieldName],
        url: URL.createObjectURL(file),
        fileName: isPdf ? (file.name || '') : '',
        isPdf,
        documentNumber: plate,
        file,
        isLocal: true
    };
};

const uploadFileToServer = (
    api: OperatorVehicleDocumentUploadApi,
    messageService: MessageService,
    file: File,
    fieldName: OperatorVehicleUploadField,
    xpressUserId: number,
    plate: string
): Promise<UploadedDocumentMeta | null> => {
    const documentType = OPERATOR_VEHICLE_FIELD_TYPE_MAP[fieldName];
    const documentFileType = OPERATOR_VEHICLE_FIELD_FILE_TYPE_MAP[fieldName];
    const formData = new FormData();
    formData.append('File', file);
    formData.append('Type', String(documentType));
    if (documentFileType != null) {
        formData.append('DocumentFileType', String(documentFileType));
    }
    if (xpressUserId > 0) {
        formData.append('XpressUserId', String(xpressUserId));
    }
    formData.append('DocumentNumber', plate);

    return api.uploadImage(formData, messageService)
        .then(data => {
            if (!data?.Success) {
                messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: data?.ErrorMessage || 'Failed to upload document.'
                });
                return null;
            }

            const isPdf = isPdfUploadFile(file);
            return {
                id: data.Id,
                type: data.Type,
                url: data.FileUrl,
                fileName: isPdf ? (data.FileName || file.name || '') : '',
                isPdf,
                documentNumber: plate
            } as UploadedDocumentMeta;
        });
};

/** Upload all locally staged files. Replaces staged rows with server rows on success. */
export const flushStagedOperatorVehicleUploads = (
    api: OperatorVehicleDocumentUploadApi,
    messageService: MessageService,
    model: Record<string, any>,
    uploadedMeta: UploadedDocumentMetaMap,
    xpressUserId: number,
    documentNumber?: string | null
): Promise<{ Success: boolean; ErrorMessage?: string; UploadedCount: number }> => {
    const plate = normalizeOperatorVehiclePlate(documentNumber);
    if (!plate) {
        return Promise.resolve({
            Success: false,
            ErrorMessage: 'Enter the plate number in Vehicle Details before uploading documents.',
            UploadedCount: 0
        });
    }

    const stagedEntries: Array<{ fieldName: OperatorVehicleUploadField; meta: UploadedDocumentMeta }> = [];
    for (const fieldName of OPERATOR_VEHICLE_UPLOAD_FIELDS) {
        for (const meta of getOperatorVehicleUploadedFiles(uploadedMeta, fieldName)) {
            if (meta.isLocal && meta.file) {
                stagedEntries.push({ fieldName, meta });
            }
        }
    }

    if (stagedEntries.length === 0) {
        return Promise.resolve({ Success: true, UploadedCount: 0 });
    }

    let uploadedCount = 0;

    return stagedEntries.reduce(
        (chain, entry) => chain.then(async previous => {
            if (!previous.Success) {
                return previous;
            }

            try {
                const fileToUpload = entry.meta.file;
                if (!fileToUpload) {
                    return {
                        Success: false,
                        ErrorMessage: 'Failed to upload one or more documents.',
                        UploadedCount: uploadedCount
                    };
                }

                const uploaded = await uploadFileToServer(
                    api,
                    messageService,
                    fileToUpload,
                    entry.fieldName,
                    xpressUserId,
                    plate
                );

                if (!uploaded) {
                    return {
                        Success: false,
                        ErrorMessage: 'Failed to upload one or more documents.',
                        UploadedCount: uploadedCount
                    };
                }

                const currentFiles = getOperatorVehicleUploadedFiles(uploadedMeta, entry.fieldName);
                const nextFiles = currentFiles.map(file =>
                    file.id === entry.meta.id ? uploaded : file
                );
                revokeLocalPreviewUrl(entry.meta.url);
                uploadedMeta[entry.fieldName] = nextFiles;
                syncOperatorVehicleUploadFieldUrl(model, entry.fieldName, nextFiles);
                uploadedCount++;
                return { Success: true, UploadedCount: uploadedCount };
            } catch (err) {
                return {
                    Success: false,
                    ErrorMessage: 'Failed to upload one or more documents.',
                    UploadedCount: uploadedCount
                };
            }
        }),
        Promise.resolve({ Success: true, UploadedCount: 0 })
    );
};

/** Revoke blob preview URLs for all locally staged files. */
export const revokeOperatorVehicleStagedPreviewUrls = (uploadedMeta: UploadedDocumentMetaMap): void => {
    for (const files of Object.values(uploadedMeta)) {
        for (const meta of files ?? []) {
            if (meta?.isLocal) {
                revokeLocalPreviewUrl(meta.url);
            }
        }
    }
};

/** Clear staged local files (revoke previews) while keeping already-persisted server rows. */
export const clearOperatorVehicleStagedUploads = (
    model: Record<string, any>,
    uploadedMeta: UploadedDocumentMetaMap
): void => {
    for (const fieldName of OPERATOR_VEHICLE_UPLOAD_FIELDS) {
        const files = getOperatorVehicleUploadedFiles(uploadedMeta, fieldName);
        if (files.length === 0) {
            continue;
        }

        const persisted = files.filter(file => !file.isLocal);
        files.filter(file => file.isLocal).forEach(file => revokeLocalPreviewUrl(file.url));

        if (persisted.length > 0) {
            uploadedMeta[fieldName] = persisted;
            syncOperatorVehicleUploadFieldUrl(model, fieldName, persisted);
        } else {
            delete uploadedMeta[fieldName];
            syncOperatorVehicleUploadFieldUrl(model, fieldName, []);
        }
    }
};

/** @deprecated Prefer flushStagedOperatorVehicleUploads on Submit. */
export const uploadOperatorVehicleDocument = (
    api: OperatorVehicleDocumentUploadApi,
    messageService: MessageService,
    model: Record<string, any>,
    uploadedMeta: UploadedDocumentMetaMap,
    file: File,
    fieldName: OperatorVehicleUploadField,
    xpressUserId: number,
    documentNumber?: string | null,
    onSuccess?: (fieldName: OperatorVehicleUploadField) => void
): void => {
    const plate = normalizeOperatorVehiclePlate(documentNumber);
    if (!plate) {
        messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Enter the plate number in Vehicle Details before uploading documents.'
        });
        return;
    }

    model[fieldName + 'IsLoading'] = true;
    void uploadFileToServer(api, messageService, file, fieldName, xpressUserId, plate)
        .then(uploaded => {
            model[fieldName + 'IsLoading'] = false;
            if (!uploaded) {
                return;
            }
            const allowsMultiple = OPERATOR_VEHICLE_MULTI_FILE_FIELDS.has(fieldName);
            const nextFiles = allowsMultiple
                ? [...getOperatorVehicleUploadedFiles(uploadedMeta, fieldName), uploaded]
                : [uploaded];
            uploadedMeta[fieldName] = nextFiles;
            syncOperatorVehicleUploadFieldUrl(model, fieldName, nextFiles);
            onSuccess?.(fieldName);
        })
        .catch(err => {
            model[fieldName + 'IsLoading'] = false;
            throw err;
        });
};

/** Returns the plate uploads were keyed under, or null if none / inconsistent. */
export const getOperatorVehicleUploadsBoundPlate = (
    uploadedMeta: UploadedDocumentMetaMap
): string | null => {
    const plates = Object.values(uploadedMeta)
        .flatMap(files => files ?? [])
        .map(meta => normalizeOperatorVehiclePlate(meta?.documentNumber))
        .filter((plate): plate is string => !!plate);

    if (plates.length === 0) {
        return null;
    }

    const first = plates[0];
    return plates.every(plate => plate === first) ? first : null;
};

export const hasOperatorVehicleUploadsBoundToDifferentPlate = (
    uploadedMeta: UploadedDocumentMetaMap,
    currentPlate: string | null | undefined
): boolean => {
    const boundPlate = getOperatorVehicleUploadsBoundPlate(uploadedMeta);
    const normalizedCurrentPlate = normalizeOperatorVehiclePlate(currentPlate);
    const allFiles = Object.values(uploadedMeta).flatMap(files => files ?? []);

    if (!boundPlate) {
        if (allFiles.length === 0) {
            return false;
        }
        return !normalizedCurrentPlate || allFiles.some(
            meta => normalizeOperatorVehiclePlate(meta?.documentNumber) !== normalizedCurrentPlate
        );
    }

    return !normalizedCurrentPlate || boundPlate !== normalizedCurrentPlate;
};

export const hasOperatorVehicleUploadedFiles = (uploadedMeta: UploadedDocumentMetaMap): boolean =>
    Object.values(uploadedMeta).some(files => (files?.length ?? 0) > 0);

/** Removes a file from local UI state only; does not call the API. Revokes blob previews. */
export const removeOperatorVehicleDocumentFileLocally = (
    model: Record<string, any>,
    uploadedMeta: UploadedDocumentMetaMap,
    fieldName: OperatorVehicleUploadField,
    fileId: number
): UploadedDocumentMeta | null => {
    const files = getOperatorVehicleUploadedFiles(uploadedMeta, fieldName);
    const meta = files.find(file => file.id === fileId) ?? null;
    if (!meta) {
        return null;
    }

    if (meta.isLocal) {
        revokeLocalPreviewUrl(meta.url);
    }

    const nextFiles = files.filter(file => file.id !== fileId);
    if (nextFiles.length > 0) {
        uploadedMeta[fieldName] = nextFiles;
    } else {
        delete uploadedMeta[fieldName];
    }
    syncOperatorVehicleUploadFieldUrl(model, fieldName, nextFiles);
    return meta;
};

export const deleteOperatorVehicleDocumentFile = (
    api: OperatorVehicleDocumentUploadApi,
    messageService: MessageService,
    model: Record<string, any>,
    uploadedMeta: UploadedDocumentMetaMap,
    fieldName: OperatorVehicleUploadField,
    fileId: number,
    xpressUserId: number,
    onSuccess?: (fieldName: OperatorVehicleUploadField) => void
): void => {
    const meta = removeOperatorVehicleDocumentFileLocally(model, uploadedMeta, fieldName, fileId);
    if (!meta) {
        messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Unable to delete: document id is missing.' });
        return;
    }

    // Local staged files were never uploaded — nothing to delete on the server.
    if (meta.isLocal) {
        onSuccess?.(fieldName);
        return;
    }

    model[fieldName + 'IsLoading'] = true;

    api.deleteImage({
        Id: meta.id,
        Type: meta.type,
        XpressUserId: xpressUserId > 0 ? xpressUserId : null
    }, messageService)
        .then(data => {
            if (!data?.Success) {
                // Restore local row if server delete failed.
                const restored = [...getOperatorVehicleUploadedFiles(uploadedMeta, fieldName), meta]
                    .sort((a, b) => a.id - b.id);
                uploadedMeta[fieldName] = restored;
                syncOperatorVehicleUploadFieldUrl(model, fieldName, restored);
                model[fieldName + 'IsLoading'] = false;
                messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: data?.ErrorMessage || 'Failed to delete document.'
                });
                return;
            }

            model[fieldName + 'IsLoading'] = false;
            onSuccess?.(fieldName);
        })
        .catch(err => {
            const restored = [...getOperatorVehicleUploadedFiles(uploadedMeta, fieldName), meta]
                .sort((a, b) => a.id - b.id);
            uploadedMeta[fieldName] = restored;
            syncOperatorVehicleUploadFieldUrl(model, fieldName, restored);
            model[fieldName + 'IsLoading'] = false;
            throw err;
        });
};

export interface FlushOperatorVehicleDocumentDeletesResult {
    Success: boolean;
    ErrorMessage?: string;
    /** Document file ids that were deleted on the server. */
    DeletedIds: number[];
    /** Document file ids that failed to delete (still present server-side). */
    FailedIds: number[];
}

export const flushOperatorVehicleDocumentDeletes = (
    api: OperatorVehicleDocumentUploadApi,
    messageService: MessageService,
    pendingDeletes: ReadonlyArray<Pick<UploadedDocumentMeta, 'id' | 'type'>>,
    xpressUserId: number
): Promise<FlushOperatorVehicleDocumentDeletesResult> => {
    if (pendingDeletes.length === 0) {
        return Promise.resolve({ Success: true, DeletedIds: [], FailedIds: [] });
    }

    // Per-item catch so one thrown delete does not reject the whole batch
    // (Promise.all would otherwise skip aggregating partial successes).
    return Promise.all(
        pendingDeletes.map(meta =>
            api.deleteImage({
                Id: meta.id,
                Type: meta.type,
                XpressUserId: xpressUserId > 0 ? xpressUserId : null
            }, messageService)
                .then(result => ({ meta, result, error: null as unknown }))
                .catch(error => ({ meta, result: null, error }))
        )
    ).then(outcomes => {
        const DeletedIds: number[] = [];
        const FailedIds: number[] = [];
        let ErrorMessage: string | undefined;

        for (const outcome of outcomes) {
            if (outcome.error || !outcome.result?.Success) {
                FailedIds.push(outcome.meta.id);
                if (!ErrorMessage) {
                    ErrorMessage = outcome.result?.ErrorMessage
                        || 'Failed to delete one or more documents.';
                }
            } else {
                DeletedIds.push(outcome.meta.id);
            }
        }

        if (FailedIds.length > 0) {
            return {
                Success: false,
                ErrorMessage: ErrorMessage || 'Failed to delete one or more documents.',
                DeletedIds,
                FailedIds
            };
        }

        return { Success: true, DeletedIds, FailedIds: [] };
    });
};

/** Deletes the first (or only) file for a field — used by single-slot vehicle photo UI. */
export const deleteOperatorVehicleDocument = (
    api: OperatorVehicleDocumentUploadApi,
    messageService: MessageService,
    model: Record<string, any>,
    uploadedMeta: UploadedDocumentMetaMap,
    fieldName: OperatorVehicleUploadField,
    xpressUserId: number,
    onSuccess?: (fieldName: OperatorVehicleUploadField) => void
): void => {
    const files = getOperatorVehicleUploadedFiles(uploadedMeta, fieldName);
    if (files.length === 0) {
        messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Unable to delete: document id is missing.' });
        return;
    }
    deleteOperatorVehicleDocumentFile(
        api,
        messageService,
        model,
        uploadedMeta,
        fieldName,
        files[0].id,
        xpressUserId,
        onSuccess
    );
};

/**
 * Stages selected files locally (blob preview). Actual upload happens on Submit via
 * {@link flushStagedOperatorVehicleUploads}.
 */
export const fileBrowseHandlerForOperatorVehicle = (
    event: any,
    fieldName: OperatorVehicleUploadField,
    messageService: MessageService,
    model: Record<string, any>,
    uploadedMeta: UploadedDocumentMetaMap,
    documentNumber?: string | null,
    options?: {
        /** Called when a persisted server file is replaced by a newly staged single-slot file. */
        onServerFileReplaced?: (meta: UploadedDocumentMeta) => void;
    }
): void => {
    const raw = event?.target?.files ? event.target.files : event;
    let files: File[] = [];
    if (raw instanceof File) {
        files = [raw];
    } else if (raw && typeof (raw as ArrayLike<File>).length === 'number') {
        files = Array.from(raw as ArrayLike<File>);
    }

    if (files.length === 0) {
        return;
    }

    const plate = normalizeOperatorVehiclePlate(documentNumber);
    if (!plate) {
        messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Enter the plate number in Vehicle Details before uploading documents.'
        });
        if (event?.target) {
            event.target.value = '';
        }
        return;
    }

    const existingFiles = getOperatorVehicleUploadedFiles(uploadedMeta, fieldName);
    if (existingFiles.some(file => file.isPdf)) {
        messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'A PDF is already uploaded for this document. Remove it before uploading another file.'
        });
        if (event?.target) {
            event.target.value = '';
        }
        return;
    }

    const allowsMultiple = OPERATOR_VEHICLE_MULTI_FILE_FIELDS.has(fieldName);
    if (!allowsMultiple && files.length > 1) {
        messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'You are uploading more than 1 image'
        });
        return;
    }

    const pdfFiles = files.filter(isPdfUploadFile);
    if (pdfFiles.length > 0 && files.length > 1) {
        messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Multiple upload is only allowed for images. Upload one PDF at a time.'
        });
        return;
    }

    // PDF is exclusive for the category — clear existing images first.
    if (pdfFiles.length > 0 && existingFiles.length > 0) {
        messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Remove existing image files before uploading a PDF for this document.'
        });
        if (event?.target) {
            event.target.value = '';
        }
        return;
    }

    if (allowsMultiple) {
        const remainingSlots = OPERATOR_VEHICLE_MAX_MULTI_FILES - existingFiles.length;
        if (remainingSlots <= 0) {
            messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: `You can upload up to ${OPERATOR_VEHICLE_MAX_MULTI_FILES} files for this document.`
            });
            if (event?.target) {
                event.target.value = '';
            }
            return;
        }
        if (files.length > remainingSlots) {
            const detail = existingFiles.length > 0
                ? `You can upload up to ${OPERATOR_VEHICLE_MAX_MULTI_FILES} files for this document. ${remainingSlots} more file(s) allowed.`
                : `You can upload up to ${OPERATOR_VEHICLE_MAX_MULTI_FILES} files for this document.`;
            messageService.add({
                severity: 'error',
                summary: 'Error',
                detail
            });
            if (event?.target) {
                event.target.value = '';
            }
            return;
        }
    }

    const filesToStage = allowsMultiple ? files : [files[0]];
    for (const file of filesToStage) {
        if (!validateUploadFile(file, messageService)) {
            if (event?.target) {
                event.target.value = '';
            }
            return;
        }
    }

    // Single-slot fields replace the previous selection (local or server).
    if (!allowsMultiple && existingFiles.length > 0) {
        for (const existing of existingFiles) {
            if (existing.isLocal) {
                revokeLocalPreviewUrl(existing.url);
            } else {
                options?.onServerFileReplaced?.(existing);
            }
        }
        delete uploadedMeta[fieldName];
    }

    const staged = filesToStage.map(file => createStagedDocumentMeta(file, fieldName, plate));
    const nextFiles = allowsMultiple
        ? [...getOperatorVehicleUploadedFiles(uploadedMeta, fieldName), ...staged]
        : staged;

    uploadedMeta[fieldName] = nextFiles;
    syncOperatorVehicleUploadFieldUrl(model, fieldName, nextFiles);

    if (event?.target) {
        event.target.value = '';
    }
};
